/**
 * Страница «Рабочее место» заказа СОУТ (`/orders/:id/sout/workplaces/:lineId`, гард company;
 * p3-sout-workplaces §4). Отдельная от общего списка страница карточки места: шапка «К списку
 * рабочих мест» + заголовок «Рабочее место — <должность>» + подпись, автосохранение (debounced
 * PATCH), возврат на шаг «Рабочие места» заказа с восстановлением позиции в списке (query
 * `?step=3&line=<id>`, читает `OrderSoutWizardPage`/`SoutWizard`). Блоки формы (§5–§12) —
 * `sout/workplace-form.tsx` (Волна 6).
 *
 * §13 «при ошибке клиент остаётся на странице»: `goBack()` (общий путь «К списку рабочих мест» и
 * «Готово — к списку рабочих мест», см. `WorkplaceForm.onBack`) НЕ полагается на дебаунс-таймер
 * (Opus-ревью Волны 6) — простой `await lineSave.flush()` ничего не делает и НЕ сообщает об
 * ошибке, если к моменту повторного клика уже нечего флашить (таймер и in-flight-промис
 * рассосались после первой неудачной попытки), поэтому повторное нажатие «Готово» после провала
 * молча ничего не повторяло бы. Вместо этого `goBack()` сам берёт на себя авторитетное
 * сохранение: `lineSave.cancel()` отменяет ещё не сработавший таймер (чтобы `flushOnUnmount` не
 * продублировал тот же PATCH после навигации), затем `saveNow()` выполняется НАПРЯМУЮ и
 * возвращает `boolean`. При неудаче — навигации нет, `saveError` уже показан, данные не потеряны;
 * КАЖДОЕ следующее нажатие «Готово»/«К списку» — новая попытка `saveNow()`.
 */
import { useEffect, useRef, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { companyApi, soutApi, type Employee, type SoutAnketa, type SoutLine } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { WorkplaceForm } from '@/sout/workplace-form'
import { SOUT_WORKPLACE_PAGE_SUBTITLE } from '@/sout/sout-copy'
import { useDebounced } from '@/sout/use-debounced'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'

export function OrderSoutWorkplacePage() {
  const navigate = useNavigate()
  const { id, lineId } = useParams<{ id: string; lineId: string }>()
  const [anketa, setAnketa] = useState<SoutAnketa | null>(null)
  const [line, setLine] = useState<SoutLine | null>(null)
  const [employees, setEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // flushOnUnmount (Opus-ревью Волны 4, MAJOR-1): уход со страницы браузерным «Назад»/закрытием
  // вкладки/переходом по ссылке ВНУТРИ debounce-окна (не через наши «К списку»/«Готово», которые
  // сами делают await flush()) не должен тихо терять последнюю правку — §4/§13 обещают
  // автосохранение. При размонтировании отложенный PATCH запускается, а не просто отменяется.
  const lineSave = useDebounced(600, { flushOnUnmount: true })
  // Актуальный снимок строки для дебаунс-сейва (flush перед возвратом на список).
  const lineRef = useRef<SoutLine | null>(null)
  // Не дёргаем setState после размонтирования (запрос из flushOnUnmount может завершиться уже
  // после ухода со страницы) — сам PATCH при этом всё равно уходит в сеть.
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )

  useEffect(() => {
    if (!id || !lineId) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    soutApi
      .getAnketa(id, controller.signal)
      .then((ank) => {
        if (controller.signal.aborted) return
        setAnketa(ank)
        const found = ank.lines.find((l) => l.id === lineId)
        if (!found) {
          setError('Рабочее место не найдено в этом заказе')
          return
        }
        setLine(found)
        lineRef.current = found
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить рабочее место'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [id, lineId])

  useEffect(() => {
    if (!anketa || !anketa.canEdit) return
    const controller = new AbortController()
    companyApi
      .listEmployees({ limit: 200, activeOnly: true }, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setEmployees(res.items)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [anketa])

  const readOnly = !anketa?.canEdit

  // Единая точка сохранения строки (§13) — используется и автосейвом (`handleChange` ниже, через
  // дебаунс на каждое изменение), и авторитетно/напрямую из `goBack()` перед уходом со страницы.
  // Возвращает `true` при успехе; при ошибке показывает `saveError` и возвращает `false` — НЕ
  // бросает исключение (дебаунс это не ждёт и не обязан обрабатывать rejection).
  async function saveNow(): Promise<boolean> {
    if (!id) return true
    const current = lineRef.current
    if (!current) return true
    if (mountedRef.current) {
      setSaving(true)
      setSaveError(null)
    }
    try {
      const saved = await soutApi.updateLine(id, current.id, {
        person: current.person,
        conditions: current.conditions,
        workplaceId: current.workplaceId,
        personId: current.personId,
      })
      // centrika-docs!100: сохранение строки со свободно введённым ФИО заводит нового сотрудника
      // в структуре, и строка возвращается уже связанной с ним. Привязку нужно забрать из ответа,
      // иначе следующий autosave снова отправил бы `personId: null` и попытался создать того же
      // человека второй раз (и получил бы дубль СНИЛС). Берём ТОЛЬКО `personId`: остальные поля
      // ответа — эхо уже отправленного, а перезапись всей строки затёрла бы то, что клиент успел
      // напечатать после начала запроса.
      if (saved.personId && !current.personId) {
        applyPersonId(current, saved.personId)
      }
      return true
    } catch (err) {
      if (mountedRef.current) setSaveError(getErrorMessage(err, 'Не удалось сохранить рабочее место'))
      return false
    } finally {
      if (mountedRef.current) setSaving(false)
    }
  }

  /**
   * Проставляет связь строки с созданным на сервере сотрудником, не трогая остальные поля:
   * правки, сделанные во время запроса, живут в `lineRef.current`, поэтому основой берётся
   * именно он, а не снимок `current`, отправленный в запрос.
   */
  function applyPersonId(sent: SoutLine, personId: string) {
    const base = lineRef.current ?? sent
    if (base.personId) return
    const next = { ...base, personId }
    lineRef.current = next
    if (mountedRef.current) setLine(next)
  }

  async function goBack() {
    // §13: авторитетное сохранение ПРЯМО СЕЙЧАС (см. комментарий модуля) — не полагаемся на
    // дебаунс-таймер/`flush()`, чтобы повторное нажатие после провала было настоящей новой
    // попыткой, а не молчаливым no-op. `cancel()` — отменяем ещё не сработавший отложенный
    // автосейв: `saveNow()` ниже уже отправит те же (актуальные) данные, дублировать не нужно.
    // Read-only (заказ отправлен) — сохранять нечего, форма и так не редактируется.
    lineSave.cancel()
    if (!readOnly) {
      const ok = await saveNow()
      if (!ok) return
    }
    if (id) navigate(`/orders/${id}/sout?step=3&line=${lineId ?? ''}`)
  }

  function handleChange(next: SoutLine) {
    setLine(next)
    lineRef.current = next
    if (!id) return
    lineSave.schedule(async () => {
      await saveNow()
    })
  }

  const title = line?.conditions.position
    ? `Рабочее место — ${line.conditions.position.toLowerCase()}`
    : 'Рабочее место'

  return (
    <div>
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void goBack()}
          leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
        >
          К списку рабочих мест
        </Button>
      </div>

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !anketa || !line ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : (
        <>
          <PageHead title={title} subtitle={SOUT_WORKPLACE_PAGE_SUBTITLE} />

          {saveError ? (
            <Alert variant="danger" className="mb-4">
              {saveError}
            </Alert>
          ) : null}
          {!readOnly ? (
            <p className="mb-3 text-12 text-txt-tertiary" aria-live="polite">
              {saving ? 'Сохранение…' : saveError ? '' : 'Черновик сохранён'}
            </p>
          ) : null}

          <WorkplaceForm
            line={line}
            readOnly={readOnly}
            employees={employees}
            onChange={handleChange}
            onBack={() => void goBack()}
            // centrika-docs!94: проверка совпадений СНИЛС идёт по рабочим местам ТЕКУЩЕГО
            // заказа — они уже загружены вместе с анкетой, отдельного запроса не нужно
            // (редактируемая строка исключается из сравнения самой формой, по id).
            orderLines={anketa.lines}
          />
        </>
      )}
    </div>
  )
}
